import Papa from "papaparse";
import {
  createEmptyInvoiceRecord,
  CSV_COLUMN_MAPPINGS,
  VALIDATION_RULES,
} from "../types/invoice";
import {
  createEmptyProductRecord,
  CSV_PRODUCT_COLUMN_MAPPINGS,
  PRODUCT_VALIDATION_RULES,
} from "../types/product";
import {
  createEmptyCustomerRecord,
  CSV_CUSTOMER_COLUMN_MAPPINGS,
  CUSTOMER_VALIDATION_RULES,
} from "../types/customer";

/**
 * CSV Parser utility for processing invoice CSV files
 */
export class CsvParser {
  constructor() {
    // Mappings will be set dynamically in parseFile
    this.columnMappings = {};
  }

  /**
   * Parses CSV file and converts to invoice records
   * @param {File} file - CSV file to parse
   * @param {string} dataType - The type of data being parsed (e.g., 'customers', 'products', 'references')
   * @returns {Promise<FileProcessingResult>} - Processing result
   */
  async parseFile(file, dataType) {
    const config = this.getConfigForDataType(dataType);
    if (!config) {
      return Promise.resolve({
        success: false,
        records: [],
        totalRecords: 0,
        validRecords: 0,
        invalidRecords: 0,
        errors: [
          {
            row: 0,
            field: "dataType",
            message: `Unsupported data type: ${dataType}`,
            value: dataType,
            type: "configuration",
          },
        ],
        metadata: {
          parser: "csv",
          fileName: file.name,
          fileSize: file.size,
          processedAt: new Date().toISOString(),
        },
      });
    }

    this.columnMappings = config.columnMappings;

    return new Promise((resolve) => {
      const errors = [];
      const records = [];
      let rowIndex = 0;

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => this.normalizeHeader(header),
        step: (result, parser) => {
          rowIndex++;

          // PapaParse has a bug where it can include an empty row at the end
          // with an error. We'll check if the data is actually empty.
          if (!result.data) {
            errors.push(`Row ${rowIndex}: No data found`);
            return;
          }

          const isTrulyEmpty = Object.values(result.data).every(
            (val) => val === "" || val === null || val === undefined
          );

          if (result.errors.length > 0 && !isTrulyEmpty) {
            result.errors.forEach((error) => {
              errors.push(`Row ${rowIndex}: ${error.message}`);
            });
            return;
          }

          if (isTrulyEmpty) {
            return;
          }

          try {
            const record = this.transformRowToRecord(
              result.data,
              rowIndex,
              config
            );
            const validationErrors = this.validateRecord(
              record,
              rowIndex,
              config.validationRules
            );
            if (validationErrors.length > 0) {
              errors.push(...validationErrors);
              record.status = "invalid";
            } else {
              record.status = "valid";
            }

            records.push(record);
          } catch (error) {
            errors.push(`Row ${rowIndex}: ${error.message}`);
          }
        },
        complete: () => {
          const validRecords = records.filter(
            (r) => r.status === "valid"
          ).length;
          const invalidRecords = records.length - validRecords;
          resolve({
            success: errors.length === 0,
            records,
            totalRecords: records.length,
            validRecords,
            invalidRecords,
            errors,
            metadata: {
              parser: "csv",
              fileName: file.name,
              fileSize: file.size,
              processedAt: new Date().toISOString(),
            },
          });
        },
        error: (error) => {
          resolve({
            success: false,
            records: [],
            totalRecords: 0,
            validRecords: 0,
            invalidRecords: 0,
            errors: [`Failed to parse CSV: ${error.message}`],
            metadata: {
              parser: "csv",
              fileName: file.name,
              fileSize: file.size,
              processedAt: new Date().toISOString(),
            },
          });
        },
      });
    });
  }

  /**
   * Normalizes CSV headers to match expected field names
   * @param {string} header - Original header
   * @returns {string} - Normalized header
   */
  normalizeHeader(header) {
    const normalized = header.toLowerCase().trim().replace(/\s+/g, "_");

    // Find matching field name from the currently set column mappings
    for (const [fieldName, variations] of Object.entries(this.columnMappings)) {
      const headers = Array.isArray(variations) ? variations : [variations];
      const normalizedHeaders = headers.map((h) =>
        h.toLowerCase().trim().replace(/\s+/g, "_")
      );
      if (normalizedHeaders.includes(normalized)) {
        return fieldName;
      }
    }

    return normalized;
  }

  /**
   * Transforms CSV row data to invoice record
   * @param {Object} rowData - Raw CSV row data
   * @param {number} rowIndex - Row index for error reporting
   * @param {Object} config - The configuration for the current data type
   * @returns {InvoiceRecord} - Transformed invoice record
   */
  transformRowToRecord(rowData, rowIndex, config) {
    const record = config.createEmptyRecord();
    record.id = `${rowIndex}`;
    // Dynamically map fields based on column mappings
    for (const [recordField, csvHeaders] of Object.entries(record)) {
      // Find the first matching header from the row data
      const headers = Array.isArray(recordField) ? recordField : [recordField];
      // The headers in rowData are normalized (lowercased, etc.) by normalizeHeader.
      const matchingHeader = headers.find(
        (h) => rowData[this.normalizeHeader(h)] !== undefined
      );
      const value = matchingHeader
        ? rowData[this.normalizeHeader(matchingHeader)]
        : undefined;

      if (value !== undefined) {
        // Basic type handling based on validation rules
        const rule = config.validationRules[recordField];
        const isNumber = rule && rule.type === "number";
        const isDate = rule && rule.type === "date";

        record[recordField] = isDate
          ? this.getDateValue(value)
          : (record[recordField] = isNumber
              ? this.getNumericValue(value)
              : this.getStringValue(value));
      }
    }

    // Store original row data in metadata
    record.metadata = {
      originalRow: rowData,
      rowIndex,
    };

    // Special handling for isActive based on status for customers
    if (config.dataType === "customers" && record.status) {
      record.isActive = (record.status || "").toUpperCase() === "ACTIVE";
    }

    return record;
  }

  /**
   * Gets string value from row data
   * @param {any} value - Raw value
   * @returns {string} - Cleaned string value
   */
  getStringValue(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  }

  /**
   * Gets numeric value from row data
   * @param {any} value - Raw value
   * @returns {number} - Parsed numeric value
   */
  getNumericValue(value) {
    if (value === null || value === undefined || value === "") return 0;

    // Remove currency symbols and commas
    const cleaned = String(value)
      .replace(/[$,€£¥]/g, "")
      .trim();
    const parsed = parseFloat(cleaned);

    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Gets date value from row data
   * @param {any} value - Raw value
   * @returns {string} - ISO date string
   */
  getDateValue(value) {
    if (!value) return "";

    try {
      // Handle DD/MM/YYYY format
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
        const [day, month, year] = value.split("/").map(Number);

        // Create date at noon to avoid timezone issues
        const date = new Date(year, month - 1, day, 12);

        if (isNaN(date.getTime())) return "";

        // Format manually to avoid timezone conversion
        return `${year}-${String(month).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`;
      }

      // Already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      // Handle other formats
      const date = new Date(value);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  }

  /**
   * Calculates missing values based on available data
   * @param {InvoiceRecord} record - Invoice record to update
   */
  calculateMissingValues(record) {
    // Calculate tax amount if missing but tax rate is available
    if (record.taxAmount === 0 && record.taxRate > 0 && record.amount > 0) {
      record.taxAmount = (record.amount * record.taxRate) / 100;
    }

    // Calculate tax rate if missing but tax amount is available
    if (record.taxRate === 0 && record.taxAmount > 0 && record.amount > 0) {
      record.taxRate = (record.taxAmount / record.amount) * 100;
    }

    // Calculate total amount if missing
    if (record.totalAmount === 0 && record.amount > 0) {
      record.totalAmount =
        record.amount + record.taxAmount - record.discountAmount;
    }

    // Validate calculated total
    const expectedTotal =
      record.amount + record.taxAmount - record.discountAmount;
    if (Math.abs(record.totalAmount - expectedTotal) > 0.01) {
      // Mark as potential discrepancy for validation
      record.metadata.calculationDiscrepancy = {
        expected: expectedTotal,
        actual: record.totalAmount,
        difference: record.totalAmount - expectedTotal,
      };
    }
  }

  /**
   * Validates invoice record against business rules
   * @param {InvoiceRecord} record - Record to validate
   * @param {number} rowIndex - Row index for error reporting
   * @param {Object} validationRules - The validation rules for the current data type
   * @returns {ProcessingError[]} - Array of validation errors
   */
  validateRecord(record, rowIndex, validationRules) {
    const errors = [];
    for (const [field, rules] of Object.entries(validationRules)) {
      const value = record[field];

      // Check required fields
      if (rules.required && (!value || value === "")) {
        errors.push(`Row ${rowIndex}: ${field} is required`);
        continue;
      }

      // Skip validation if field is empty and not required
      if (!value && !rules.required) continue;

      // Type validation
      if (
        rules.type === "number" &&
        (isNaN(value) || typeof value !== "number")
      ) {
        errors.push(`Row ${rowIndex}: ${field} must be a valid number`);
        continue;
      }

      if (rules.type === "date" && !this.isValidDate(value)) {
        errors.push(`Row ${rowIndex}: ${field} must be a valid date`);
        continue;
      }

      // Range validation for numbers
      if (rules.type === "number") {
        if (rules.min !== undefined && value < rules.min) {
          errors.push(
            `Row ${rowIndex}: ${field} must be at least ${rules.min}`
          );
        }

        if (rules.max !== undefined && value > rules.max) {
          errors.push(`Row ${rowIndex}: ${field} must not exceed ${rules.max}`);
        }
      }

      // String length validation
      if (typeof value === "string") {
        if (rules.minLength && value.length < rules.minLength) {
          errors.push(
            `Row ${rowIndex}: ${field} must be at least ${rules.minLength} characters`
          );
        }

        if (rules.maxLength && value.length > rules.maxLength) {
          errors.push(
            `Row ${rowIndex}: ${field} must not exceed ${rules.maxLength} characters`
          );
        }
      }

      // Pattern validation
      if (
        rules.pattern &&
        typeof value === "string" &&
        !rules.pattern.test(value)
      ) {
        errors.push(`${field} format is invalid`);
      }
    }

    return errors;
  }

  /**
   * Checks if a value is a valid date
   * @param {any} value - Value to check
   * @returns {boolean} - True if valid date
   */
  isValidDate(value) {
    if (!value) return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  /**
   * Gets the configuration object for a given data type.
   * @param {string} dataType - The type of data (e.g., 'customers', 'products').
   * @returns {Object|null} A configuration object or null if not found.
   */
  getConfigForDataType(dataType) {
    switch (dataType) {
      case "customers":
        return {
          dataType: "customers",
          createEmptyRecord: createEmptyCustomerRecord,
          columnMappings: CSV_CUSTOMER_COLUMN_MAPPINGS.standard,
          validationRules: CUSTOMER_VALIDATION_RULES,
        };
      case "products":
        return {
          dataType: "products",
          createEmptyRecord: createEmptyProductRecord,
          columnMappings: CSV_PRODUCT_COLUMN_MAPPINGS.standard,
          validationRules: PRODUCT_VALIDATION_RULES,
        };
      case "references":
        return {
          dataType: "references",
          createEmptyRecord: createEmptyInvoiceRecord,
          columnMappings: CSV_COLUMN_MAPPINGS.standard,
          validationRules: VALIDATION_RULES,
        };
      default:
        return null;
    }
  }
}
